/**
 * SectionForm - Form for adding/editing custom sections
 * Enhanced with channel/conference filtering and YouTube URL support
 */
import { useState, useEffect } from 'react';
import { Layers, Save, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { API, authHeaders } from '../utils';

export const SectionForm = ({
  sectionFormData,
  setSectionFormData,
  editingSection,
  onSubmit,
  onCancel,
  saving
}) => {
  const [channelOptions, setChannelOptions] = useState({ channels: [], conference_networks: [] });

  useEffect(() => {
    fetch(`${API}/watch/admin/channels-for-filter`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : { channels: [], conference_networks: [] })
      .then(setChannelOptions)
      .catch(() => {});
  }, []);

  return (
    <form onSubmit={onSubmit} className="bg-zinc-900 rounded-xl p-6 space-y-4 border border-white/10" data-testid="section-form">
      <h3 className="text-white font-bold flex items-center gap-2">
        <Layers className="w-5 h-5 text-orange-500" />
        {editingSection ? 'Edit Section' : 'Add Custom Section'}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-zinc-400 text-sm mb-1">Section Title *</label>
          <input
            type="text"
            value={sectionFormData.title}
            onChange={(e) => setSectionFormData({ ...sectionFormData, title: e.target.value })}
            placeholder="e.g., Basketball Highlights, Football Replays"
            className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-2 rounded"
            data-testid="section-title-input"
            required
          />
        </div>
        <div>
          <label className="block text-zinc-400 text-sm mb-1">Section Type</label>
          <select
            value={sectionFormData.section_type}
            onChange={(e) => setSectionFormData({ ...sectionFormData, section_type: e.target.value })}
            className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-2 rounded"
            data-testid="section-type-select"
          >
            <option value="games">Games/Broadcasts</option>
            <option value="videos">Featured Videos</option>
            <option value="recordings">On-Demand Recordings</option>
            <option value="custom">Custom (Manual)</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-zinc-400 text-sm mb-1">Sport Filter</label>
          <select
            value={sectionFormData.sport_filter}
            onChange={(e) => setSectionFormData({ ...sectionFormData, sport_filter: e.target.value })}
            className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-2 rounded"
            data-testid="section-sport-filter"
          >
            <option value="">All Sports</option>
            <option value="Football">Football</option>
            <option value="Basketball">Basketball</option>
            <option value="Men's Basketball">Men's Basketball</option>
            <option value="Women's Basketball">Women's Basketball</option>
            <option value="Soccer">Soccer</option>
            <option value="Baseball">Baseball</option>
            <option value="Volleyball">Volleyball</option>
            <option value="Softball">Softball</option>
            <option value="Track & Field">Track & Field</option>
            <option value="Swimming">Swimming</option>
            <option value="Wrestling">Wrestling</option>
            <option value="Lacrosse">Lacrosse</option>
            <option value="Hockey">Hockey</option>
          </select>
        </div>
        <div>
          <label className="block text-zinc-400 text-sm mb-1">Channel / Network Filter</label>
          <select
            value={sectionFormData.channel_filter || ''}
            onChange={(e) => setSectionFormData({ ...sectionFormData, channel_filter: e.target.value })}
            className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-2 rounded"
            data-testid="section-channel-filter"
          >
            <option value="">All Channels</option>
            {channelOptions.channels.length > 0 && (
              <optgroup label="Channels">
                {channelOptions.channels.map(ch => (
                  <option key={ch.id} value={ch.id}>{ch.name}</option>
                ))}
              </optgroup>
            )}
            {channelOptions.conference_networks.length > 0 && (
              <optgroup label="Conference Networks">
                {channelOptions.conference_networks.map(cn => (
                  <option key={cn.id} value={cn.id}>{cn.name}</option>
                ))}
              </optgroup>
            )}
          </select>
        </div>
        <div>
          <label className="block text-zinc-400 text-sm mb-1">Max Items</label>
          <input
            type="number"
            value={sectionFormData.max_items}
            onChange={(e) => setSectionFormData({ ...sectionFormData, max_items: parseInt(e.target.value) || 10 })}
            min={1}
            max={50}
            className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-2 rounded"
          />
        </div>
      </div>

      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 text-zinc-400 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={sectionFormData.is_active}
            onChange={(e) => setSectionFormData({ ...sectionFormData, is_active: e.target.checked })}
            className="w-4 h-4 rounded"
          />
          Active
        </label>
        {sectionFormData.section_type === 'games' && (
          <>
            <label className="flex items-center gap-2 text-zinc-400 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={sectionFormData.show_live_only}
                onChange={(e) => setSectionFormData({ ...sectionFormData, show_live_only: e.target.checked })}
                className="w-4 h-4 rounded"
              />
              Live Only
            </label>
            <label className="flex items-center gap-2 text-zinc-400 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={sectionFormData.show_upcoming_only}
                onChange={(e) => setSectionFormData({ ...sectionFormData, show_upcoming_only: e.target.checked })}
                className="w-4 h-4 rounded"
              />
              Upcoming Only
            </label>
          </>
        )}
      </div>

      <div className="flex gap-2 pt-2">
        <Button type="submit" disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="section-submit-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {editingSection ? 'Update Section' : 'Add Section'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default SectionForm;
